import { io, type Socket } from 'socket.io-client';
import type { ServerEvents, ServerEventName } from '@gbv/types';
import { getAccessToken } from './api';

/**
 * One socket per session. Rooms are decided server-side from the user's
 * memberships, so there is nothing to configure here beyond the token.
 */
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? 'http://localhost:4001';

let socket: Socket | null = null;

export function connectSocket(): Socket | null {
  const token = getAccessToken();
  if (!token) return null;
  if (socket?.connected) return socket;

  socket = io(SOCKET_URL, {
    auth: { token, portal: 'EMPLOYEE' },
    // Polling stays in the list: a 4G connection in a shop drops websockets.
    transports: ['websocket', 'polling'],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });

  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

export function getSocket(): Socket | null {
  return socket;
}

/** Subscribe to a server event; returns the unsubscribe function. */
export function onServerEvent<E extends ServerEventName>(
  event: E,
  handler: (payload: ServerEvents[E]) => void,
): () => void {
  const active = socket;
  if (!active) return () => {};

  active.on(event as string, handler as (...args: unknown[]) => void);
  return () => {
    active.off(event as string, handler as (...args: unknown[]) => void);
  };
}

/** Join the WhatsApp inbox room, only while the inbox is on screen. */
export function subscribeToInbox(orgId: string): () => void {
  socket?.emit('org:subscribe', { orgId });
  return () => {
    socket?.emit('org:unsubscribe', { orgId });
  };
}
